import type { LegacyPreset } from "./presets";
import type { VoiceSettings } from "./types";

export const STORE_KEY = "toonspeech-prefs";

export const DEFAULT_SETTINGS: VoiceSettings = {
  stability: 0.5,
  similarity_boost: 0.75,
  style: 0.0,
  speed: 1.0,
  use_speaker_boost: true,
};

export interface Prefs {
  voice_id?: string;
  model_id?: string;
  settings?: Partial<VoiceSettings>;
  /* Presets from before they moved to the server, kept only long enough to
     offer an import. See presets.ts. */
  presets?: LegacyPreset[];
  /* The setup checklist hides itself once every step is done; this is for the
     admin who wants it gone before then. Per browser, like the rest of this
     file — it is a nudge, not account state worth a column. */
  hide_first_run?: boolean;
  /* The composer's unsent text. Per browser, and per instance for free: every
     instance is its own subdomain and so its own localStorage. */
  draft?: string;
  /* Generate first and listen before anything reaches the stream. */
  preview_first?: boolean;
  /* Set once the "overlay connected" moment has been celebrated here. */
  celebrated_overlay?: boolean;
}

export function loadPrefs(): Prefs {
  try {
    return (JSON.parse(localStorage.getItem(STORE_KEY) || "{}") as Prefs) || {};
  } catch {
    return {};
  }
}

/** Merges, rather than replaces: the composer saves its three fields on every
    change, and a whole-object write there would keep wiping anything stored by
    someone else. */
export function savePrefs(prefs: Prefs): void {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify({ ...loadPrefs(), ...prefs }));
  } catch {
    /* private mode, quota - the panel still works, it just forgets */
  }
}

/* ---------------------------------------------------------------- theme */
/* "system" simply removes the attribute and lets prefers-color-scheme win.
   The initial value is applied by the inline script in index.html, before
   React mounts, so there is no flash of the wrong theme. */

export const THEME_KEY = "toonspeech-theme";
export type Theme = "light" | "dark" | "system";

export function loadTheme(): Theme {
  const saved = localStorage.getItem(THEME_KEY);
  return saved === "light" || saved === "dark" ? saved : "system";
}

export function applyTheme(choice: Theme): void {
  if (choice === "light" || choice === "dark") {
    document.documentElement.dataset.theme = choice;
    localStorage.setItem(THEME_KEY, choice);
  } else {
    delete document.documentElement.dataset.theme;
    localStorage.removeItem(THEME_KEY);
  }
}

/* -------------------------------------------------------------- density */
/* Per browser, like the theme, because it is about the window the panel is in
   — a sidebar next to OBS wants it tight, a second monitor does not — not
   about the instance. Applied before first paint by index.html. */

export const DENSITY_KEY = "toonspeech-density";
export type Density = "comfortable" | "compact";

export function loadDensity(): Density {
  try {
    return localStorage.getItem(DENSITY_KEY) === "compact" ? "compact" : "comfortable";
  } catch {
    return "comfortable";
  }
}

export function applyDensity(choice: Density): void {
  if (choice === "compact") document.documentElement.dataset.density = "compact";
  else delete document.documentElement.dataset.density;
  try {
    if (choice === "compact") localStorage.setItem(DENSITY_KEY, choice);
    else localStorage.removeItem(DENSITY_KEY);
  } catch {
    /* private mode: it applies for this visit and is forgotten */
  }
}
